import React, { createContext, useEffect, useRef, useState } from "react";
import { w3cwebsocket as W3CWebSocket } from "websocket";
import useAuth from "../hooks/useAuth";
import axios from "../utils/axios";

// Default matches the [isReady, val, send, disconnect, isOnCall, setIsOnCall] shape every
// consumer destructures, so useSocket() stays safely inert (never throws) when used
// outside <WebsocketProvider>.
const WebsocketContext = createContext([false, null, () => {}, false, false, () => {}]);

// Actions that mean a PBX/dialer call leg has just been placed for the agent.
const CALL_START_ACTIONS = [
  "call_customer",
  "call_manual_customer",
  "progressive_dialer_start",
  "predictive_dialer_start",
];
// Actions that carry a callstatus of "end" once that call leg finishes.
const CALL_STATUS_ACTIONS = [
  "call_status",
  "progressive_call_status",
  "predictive_call_status",
  "progressive_call_completed",
  "predictive_call_completed",
];

export const WebsocketProvider = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [val, setVal] = useState(null);
  // Mirrors Call.js's own call-lifecycle tracking so any consumer (not just
  // Call.js) can know whether the agent is on a non-WebRTC/PBX call.
  const [isOnCall, setIsOnCall] = useState(false);
  const { auth } = useAuth();
  const ws = useRef(null);
  const heartbeatInterval = useRef(null);
  const heartbeatTimeout = useRef(null);
  const messageQueue = useRef([]);
  const isDrainingQueue = useRef(false);

  const [diconnect, setDisconnect] = useState(false);
  const BASE_URL = process.env.REACT_APP_API_URL;

  const QUEUE_SOCKET = "ws_disconnected_queue"

  const addToWebsocketDisconnectQueue = () => {
    const existing = JSON.parse(localStorage.getItem(QUEUE_SOCKET) || "[]");

    //If already tracking a disconnect, DO NOTHING
    if (existing.length > 0 && !existing[0].connected_time_after_disconnect) {
      return;
    }

    //Start a NEW session
    const newEntry = {
      "user_id": auth?.user_details?.user || "",
      "ip_address": "0.0.0.0",
      "first_disconnected_time": new Date().toISOString(),
      "connected_time_after_disconnect": ""
    };

    localStorage.setItem(QUEUE_SOCKET, JSON.stringify([newEntry]));
  };

  const updateReconnectTime = () => {
    const existing = JSON.parse(localStorage.getItem(QUEUE_SOCKET) || "[]");

    if (!existing.length) return null;

    if (!existing[0].connected_time_after_disconnect) {
      existing[0].connected_time_after_disconnect = new Date().toISOString();
      localStorage.setItem(QUEUE_SOCKET, JSON.stringify(existing));
    }

    return existing[0]; // ✅ return session
  };

  const clearQueue = ()=>{
    localStorage.removeItem(QUEUE_SOCKET);
  }

  // setVal only ever holds ONE message. If two socket "message" events fire
  // back-to-back before React commits/runs effects for the first setVal,
  // the second setVal silently overwrites it and consumers (e.g. Call.js)
  // never see the dropped message at all. Draining one message per state
  // update - each spaced out on its own macrotask - guarantees every
  // message gets its own commit + effect run, so none are lost.
  const drainMessageQueue = () => {
    if (isDrainingQueue.current) return;
    isDrainingQueue.current = true;

    const step = () => {
      if (messageQueue.current.length === 0) {
        isDrainingQueue.current = false;
        return;
      }
      const next = messageQueue.current.shift();
      setVal(next);
      setTimeout(step, 0);
    };

    step();
  };

  const sentQueueToServer = async (sessionParam) => {
    try {
      const session =
        sessionParam ||
        JSON.parse(localStorage.getItem(QUEUE_SOCKET) || "[]")[0];

      if (!session) return;
      if (!session.connected_time_after_disconnect) return;

      const res = await axios.post(
        BASE_URL + "/contactcentre/WebsocketDisconnectionLogging/",
        { event: session }
      );

      clearQueue();

      console.log("✅ Session sent", res.data);
    } catch (error) {
      console.error("❌ websocket history update error:", error);
    }
  };

  let user_id = "";
  if (auth?.user_details?.type === "agent") {
    user_id = auth?.user_details?.user;
  }
  else if (auth?.user_details?.type === "contact-admin") {
    user_id = auth?.user_details?.id
  } else {
    user_id = auth?.user_details?.user;
  }

  useEffect(() => {
    if (!user_id) {
      // auth not ready yet — wait for user_id to become available instead of
      // connecting with an invalid/"undefined" id
      return;
    }

    let isUnmounted = false;
    let reconnectTimer = null;
    let retryCount = 0;
    const MAX_RECONNECT_DELAY = 30000; // 30s cap

    function scheduleReconnect() {
      if (isUnmounted) return;
      const delay = Math.min(2000 * 2 ** retryCount, MAX_RECONNECT_DELAY);
      retryCount += 1;
      reconnectTimer = setTimeout(connect, delay);
    }

    function connect() {
      if (isUnmounted) return;

      console.log("======== WebSocket URL =========", process.env.REACT_APP_WS_URL, "++++", user_id);

      let socket;
      try {
        socket = new W3CWebSocket(process.env.REACT_APP_WS_URL + "ws/campaign/" + user_id + "/");
      } catch (error) {
        console.error("WebSocket creation failed:", error);
        scheduleReconnect();
        return;
      }

      socket.addEventListener("open", function () {
        console.log("WebSocket connected");
        retryCount = 0;
        setIsReady(true);
        startHeartbeat(socket);

        const session = updateReconnectTime();
        sentQueueToServer(session);
      });

      socket.addEventListener("message", function (message) {
        if (message.data === "pong") {
          // console.log("✅ Heartbeat received");
          setDisconnect(false)
          clearTimeout(heartbeatTimeout.current);
        } else {
          const parsed = JSON.parse(message.data);
          messageQueue.current.push(parsed);
          drainMessageQueue();

          const action = parsed?.action;
          const callstatus = parsed?.data?.callstatus;
          if (CALL_START_ACTIONS.includes(action)) {
            setIsOnCall(true);
          } else if (CALL_STATUS_ACTIONS.includes(action) && callstatus === "end") {
            setIsOnCall(false);
          }
        }
      });

      socket.addEventListener("close", function () {
        console.log("WebSocket disconnected ❌");
        setIsReady(false);
        setDisconnect(true);
        // A dropped connection can swallow the "call ended" message (e.g. the
        // heartbeat-timeout force-close above), leaving isOnCall stuck true
        // forever with no way to recover it. Don't trust it across a reconnect.
        setIsOnCall(false);
        stopHeartbeat();
        addToWebsocketDisconnectQueue();
        scheduleReconnect();
      });

      socket.onerror = (e) => {
        console.error("WebSocket error:", e);
      };

      ws.current = socket;
    }

    connect();

    return () => {
      isUnmounted = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      stopHeartbeat();
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [user_id]);

  /** Start the Heartbeat Mechanism **/
  function startHeartbeat(socket) {
    heartbeatInterval.current = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        // console.log("Sending heartbeat...");
        socket.send("ping");

        heartbeatTimeout.current = setTimeout(() => {
          console.warn("🚨 No heartbeat response, closing WebSocket...");
          socket.close(); // Force close if no response
        }, 10000);
      }
    }, 30000); // Ping every 30 seconds
  }

  /** Stop Heartbeat on Disconnect **/
  function stopHeartbeat() {
    clearInterval(heartbeatInterval.current);
    clearTimeout(heartbeatTimeout.current);
  }

  const ret = [isReady, val, ws.current?.send.bind(ws.current), diconnect, isOnCall, setIsOnCall];

  return <WebsocketContext.Provider value={ret}>{children}</WebsocketContext.Provider>;
};

export default WebsocketContext;
